import { useMutation } from '@apollo/client/react';
import * as Sentry from '@sentry/react';

import {
  APPOINTMENT_ID,
  APPOINTMENT_TOKEN,
  APPOINTMENT_USER_ID,
  APPOINTMENT_USER_TYPE,
} from '@constants/storageKeys';
import {
  AppointmentAttribution,
  AppointmentParticipantType,
  AppointmentType,
  CreateAppointmentMutation,
} from '@generated/gql/graphql';
import { useSetAppointmentSession } from '@store/appointment/selectors';

import { createAppointment } from './query';

const setStorage = (
  token: string,
  appointmentId: string,
  userId: string,
  userType: AppointmentParticipantType
) => {
  localStorage.setItem(APPOINTMENT_TOKEN, token);
  localStorage.setItem(APPOINTMENT_ID, appointmentId);
  localStorage.setItem(APPOINTMENT_USER_ID, userId);
  localStorage.setItem(APPOINTMENT_USER_TYPE, userType);
};

const useRemoteStarted = () => {
  const setAppointmentSession = useSetAppointmentSession();
  const [startRequest, { loading, error }] = useMutation(createAppointment);

  const handleRemoteStartSuccess = (data: CreateAppointmentMutation) => {
    const {
      appointment: { id: appointmentId },
      me: { id: userId, type: userType, token },
    } = data.createAppointment;

    setStorage(token, appointmentId, userId, userType);

    Sentry.setTag('appointment_participant_id', userId);
    Sentry.setTag('appointment_participant_type', userType);
    Sentry.setTag('appointment_id', appointmentId);

    setAppointmentSession({
      appointmentId,
      userId,
      userType,
    });
  };

  const startAppointment = (
    appointmentType: AppointmentType,
    attribution: Nullable<AppointmentAttribution>
  ) =>
    startRequest({
      variables: {
        appointmentType,
        attribution,
      },
      onCompleted: handleRemoteStartSuccess,
    });

  return {
    startAppointment,
    isLoading: loading,
    error,
  };
};

export { useRemoteStarted };
